import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../services/api.ts';
import LoadingSpinner from '../../components/LoadingSpinner.tsx';
import ErrorBanner from '../../components/ErrorBanner.tsx';
import SuccessBanner from '../../components/SuccessBanner.tsx';
import H2GOLogo from '../../components/H2GOLogo.tsx';

interface Contact {
  id: number;
  name: string;
  email: string;
  message: string;
  domain: string | null;
  ipAddress: string | null;
  status: 'new' | 'read' | 'replied' | 'closed';
  adminNotes: string | null;
  repliedAt: string | null;
  repliedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const AdminContacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [updating, setUpdating] = useState<number | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);

  // Fetch contacts
  const fetchContacts = async (page = 1, status = '', search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = { page, limit: 10 };
      if (status) params.status = status;
      if (search) params.search = search;

      const response = await contactAPI.getAllContacts(params);
      
      if (response.data.success) {
        setContacts(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch contacts');
      }
    } catch (error: any) {
      console.error('Error fetching contacts:', error);
      setError(error.response?.data?.message || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  // Update contact status
  const updateContactStatus = async (id: number, status: string, notes?: string) => {
    try {
      setUpdating(id);
      const response = await contactAPI.updateContactStatus(id, { status, adminNotes: notes });
      
      if (response.data.success) {
        // Update the contact in the list
        setContacts(prev => prev.map(contact => 
          contact.id === id 
            ? { ...contact, status: status as any, adminNotes: notes || contact.adminNotes, updatedAt: new Date().toISOString() }
            : contact
        ));
        setShowModal(false);
        setSelectedContact(null);
        setSuccess('Contact status updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update contact status');
      }
    } catch (error: any) {
      console.error('Error updating contact:', error);
      setError(error.response?.data?.message || 'Failed to update contact status');
    } finally {
      setUpdating(null);
    }
  };

  // Delete contact
  const deleteContact = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) {
      return;
    }

    try {
      setUpdating(id);
      const response = await contactAPI.deleteContact(id);
      
      if (response.data.success) {
        setContacts(prev => prev.filter(contact => contact.id !== id));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
        setSuccess('Contact deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete contact');
      }
    } catch (error: any) {
      console.error('Error deleting contact:', error);
      setError(error.response?.data?.message || 'Failed to delete contact');
    } finally {
      setUpdating(null);
    }
  };

  // Bulk actions
  const handleSelectContact = (id: number) => {
    setSelectedContacts(prev => 
      prev.includes(id) 
        ? prev.filter(contactId => contactId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
    }
  };

  const bulkUpdateStatus = async (status: string) => {
    if (selectedContacts.length === 0) return;

    try {
      setUpdating(-1); // Special ID for bulk operations
      const promises = selectedContacts.map(id => 
        contactAPI.updateContactStatus(id, { status })
      );
      
      await Promise.all(promises);
      
      // Update local state
      setContacts(prev => prev.map(contact => 
        selectedContacts.includes(contact.id)
          ? { ...contact, status: status as any, updatedAt: new Date().toISOString() }
          : contact
      ));
      
      setSelectedContacts([]);
      setSuccess(`${selectedContacts.length} contacts updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error bulk updating contacts:', error);
      setError('Failed to update selected contacts');
    } finally {
      setUpdating(null);
    }
  };

  const bulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contact submissions?`)) {
      return;
    }

    try {
      setUpdating(-1);
      const promises = selectedContacts.map(id => contactAPI.deleteContact(id));
      
      await Promise.all(promises);
      
      setContacts(prev => prev.filter(contact => !selectedContacts.includes(contact.id)));
      setPagination(prev => ({ ...prev, total: prev.total - selectedContacts.length }));
      setSelectedContacts([]);
      setSuccess(`${selectedContacts.length} contacts deleted successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error bulk deleting contacts:', error);
      setError('Failed to delete selected contacts');
    } finally {
      setUpdating(null);
    }
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchContacts(1, statusFilter, searchTerm);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    fetchContacts(1, status, searchTerm);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchContacts(page, statusFilter, searchTerm);
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'read': return 'bg-yellow-100 text-yellow-800';
      case 'replied': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Truncate message
  const truncateMessage = (message: string, maxLength = 100) => {
    return message.length > maxLength ? message.substring(0, maxLength) + '...' : message;
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  if (loading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <H2GOLogo className="h-12 w-auto text-blue-600" />
            <h1 className="text-4xl font-extrabold text-gray-900">Contact Management</h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-lg font-medium text-gray-700">Total Contacts: {pagination.total}</p>
              <p className="text-sm text-gray-500">Manage contact submissions</p>
            </div>
          </div>
        </div>

      {/* Success/Error Banners */}
      {success && (
        <SuccessBanner 
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}
      {error && (
        <ErrorBanner 
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                fetchContacts(1, '', '');
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedContacts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedContacts.length} contact{selectedContacts.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => bulkUpdateStatus('read')}
                  disabled={updating === -1}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50"
                >
                  Mark as Read
                </button>
                <button
                  onClick={() => bulkUpdateStatus('replied')}
                  disabled={updating === -1}
                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 disabled:opacity-50"
                >
                  Mark as Replied
                </button>
                <button
                  onClick={() => bulkUpdateStatus('closed')}
                  disabled={updating === -1}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  Mark as Closed
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={updating === -1}
                  className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 disabled:opacity-50"
                >
                  Delete Selected
                </button>
              </div>
            </div>
            <button
              onClick={() => setSelectedContacts([])}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner size="md" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">No contact submissions found</div>
            <div className="text-gray-400 text-sm mt-1">
              {searchTerm || statusFilter ? 'Try adjusting your filters' : 'New submissions will appear here'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedContacts.length === contacts.length && contacts.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CONTACT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MESSAGE
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SUBMITTED
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <tr key={contact.id} className={`hover:bg-gray-50 ${selectedContacts.includes(contact.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {contact.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.email}</div>
                          {contact.domain && (
                            <div className="text-xs text-gray-400">From: {contact.domain}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {truncateMessage(contact.message)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(contact.status)}`}>
                        {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(contact.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedContact(contact);
                          setShowModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        disabled={updating === contact.id}
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                      <button
                        onClick={() => deleteContact(contact.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={updating === contact.id}
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {updating === contact.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
              {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Contact Detail Modal */}
      {showModal && selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onClose={() => {
            setShowModal(false);
            setSelectedContact(null);
          }}
          onUpdate={updateContactStatus}
          updating={updating === selectedContact.id}
        />
        )}
      </div>
    </div>
  );
};

// Contact Detail Modal Component
interface ContactDetailModalProps {
  contact: Contact;
  onClose: () => void;
  onUpdate: (id: number, status: string, notes?: string) => void;
  updating: boolean;
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({ contact, onClose, onUpdate, updating }) => {
  const [status, setStatus] = useState(contact.status);
  const [notes, setNotes] = useState(contact.adminNotes || '');

  const handleUpdate = () => {
    onUpdate(contact.id, status, notes);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Contact Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <div className="mt-1 text-sm text-gray-900">{contact.name}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1 text-sm text-gray-900">
                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                  {contact.email}
                </a>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Message</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {contact.message}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Domain</label>
                <div className="mt-1 text-sm text-gray-900">{contact.domain || 'N/A'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">IP Address</label>
                <div className="mt-1 text-sm text-gray-900">{contact.ipAddress || 'N/A'}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted</label>
              <div className="mt-1 text-sm text-gray-900">{new Date(contact.createdAt).toLocaleString()}</div>
            </div>

            {/* Status Update */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Admin Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Admin Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add internal notes about this contact..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={updating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
